<template>
  <page-main
    title="Interests"
    nextText="Languages"
    buttonTabIndex="3"
    eventName="nav"
    @nav="nav('/Languages')"
    @save="save()"
  >
    <template v-slot:aside>
      What are your passions or interests outside of your career or academics?
      <ul>
        <li>
          This is where you can add the interesting things you do outside of
          school or work that are appropriate to share with potential employers.
        </li>
      </ul>
    </template>
    <template v-slot:nav>
      <v-system-bar color="accent" height="5" class="mt-4"></v-system-bar>
      <modal cardTitle="Example" ctaText="Need an example?">
        <ul class="font-italic">
          <li class="mb-4">
            <span class="font-weight-bold">Example #1:</span>
            <ul>
              <li>
                <span class="font-weight-bold">Career Interest:</span>
                Business Analysis (Function) — Working in it · Skills: SQL,
                requirements gathering, stakeholder management
              </li>
              <li>
                <span class="font-weight-bold">Personal Interest:</span>
                Hiking (Physical) — Section-hiked the AT 2019–2024; discuss in
                interviews
              </li>
            </ul>
          </li>
        </ul>
      </modal>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <r-tip-container :tips="tips"></r-tip-container>
      </modal>
    </template>

    <v-container class="fade-in">
      <!-- ═════════════════ CAREER INTERESTS ═════════════════ -->
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-1 font-weight-bold"
              >Career Interests</span
            >
            <v-btn color="primary" small @click="openCareerDialog()">
              <v-icon left small>mdi-plus</v-icon> Add Career Interest
            </v-btn>
          </div>

          <!-- PR-INTERESTS-V2: legacy migration banner — same shape as the
               Credentials banner on Organizations.vue. Surfaces only when
               there's something to migrate. Promotes flat StringItem-shaped
               interests into the structured form and clears the legacy
               list once the saves succeed. -->
          <v-alert
            v-if="legacyCareerCount > 0"
            type="info"
            text
            dense
            class="mb-3 r-card"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong
                  >{{ legacyCareerCount }} legacy career interest{{
                    legacyCareerCount !== 1 ? "s" : ""
                  }}</strong
                >
                can be migrated to richer entries (category, timeline,
                experience level, why it matters).
              </div>
              <v-btn
                small
                color="primary"
                class="ml-3"
                :loading="isMigratingCareer"
                @click="migrateLegacyCareer"
              >
                <v-icon left small>mdi-transfer-right</v-icon>
                Migrate All
              </v-btn>
            </div>
          </v-alert>

          <div
            v-if="
              structuredCareerInterests.length === 0 && legacyCareerCount === 0
            "
            class="empty-state"
          >
            <v-icon class="empty-state-icon">mdi-compass-outline</v-icon>
            <div class="empty-state-title">No career interests yet</div>
            <div class="empty-state-subtitle">
              Add the roles, industries, and skills you're growing toward.
            </div>
          </div>

          <v-card
            v-for="(ci, idx) in structuredCareerInterests"
            :key="'ci-' + idx"
            outlined
            class="mb-2 pa-3 r-card"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="font-weight-bold">{{ ci.name }}</span>
                <v-chip
                  v-if="ci.category"
                  x-small
                  class="ml-2"
                  color="secondary"
                  outlined
                  >{{ careerCategoryLabel(ci.category) }}</v-chip
                >
                <v-chip
                  v-if="ci.experienceLevel"
                  x-small
                  class="ml-1"
                  color="accent"
                  outlined
                  >{{ experienceLevelLabel(ci.experienceLevel) }}</v-chip
                >
                <v-chip
                  v-if="ci.targetTimeline"
                  x-small
                  class="ml-1"
                  outlined
                  >{{ timelineLabel(ci.targetTimeline) }}</v-chip
                >
              </div>
              <div>
                <v-btn icon x-small @click="openCareerDialog(idx)"
                  ><v-icon small>mdi-pencil</v-icon></v-btn
                >
                <v-btn icon x-small @click="removeCareer(idx)"
                  ><v-icon small color="error">mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
            <div v-if="ci.whyItMatters" class="text-caption grey--text mt-1">
              {{ ci.whyItMatters }}
            </div>
            <div
              v-if="ci.associatedSkills && ci.associatedSkills.length"
              class="mt-1"
            >
              <v-chip
                v-for="(skill, si) in ci.associatedSkills"
                :key="'ci-sk-' + idx + '-' + si"
                x-small
                class="mr-1"
                color="accent"
                outlined
                >{{ skill }}</v-chip
              >
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <!-- ═════════════════ PERSONAL INTERESTS ═════════════════ -->
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-1 font-weight-bold"
              >Personal Interests</span
            >
            <v-btn color="primary" small @click="openPersonalDialog()">
              <v-icon left small>mdi-plus</v-icon> Add Personal Interest
            </v-btn>
          </div>

          <v-alert
            v-if="legacyPersonalCount > 0"
            type="info"
            text
            dense
            class="mb-3 r-card"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong
                  >{{ legacyPersonalCount }} legacy personal interest{{
                    legacyPersonalCount !== 1 ? "s" : ""
                  }}</strong
                >
                can be migrated to richer entries (category, detail,
                professional relevance).
              </div>
              <v-btn
                small
                color="primary"
                class="ml-3"
                :loading="isMigratingPersonal"
                @click="migrateLegacyPersonal"
              >
                <v-icon left small>mdi-transfer-right</v-icon>
                Migrate All
              </v-btn>
            </div>
          </v-alert>

          <div
            v-if="
              structuredPersonalInterests.length === 0 &&
                legacyPersonalCount === 0
            "
            class="empty-state"
          >
            <v-icon class="empty-state-icon">mdi-heart-outline</v-icon>
            <div class="empty-state-title">No personal interests yet</div>
            <div class="empty-state-subtitle">
              Add the things you do outside of work that round out who you are.
            </div>
          </div>

          <v-card
            v-for="(pi, idx) in structuredPersonalInterests"
            :key="'pi-' + idx"
            outlined
            class="mb-2 pa-3 r-card"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="font-weight-bold">{{ pi.name }}</span>
                <v-chip
                  v-if="pi.category"
                  x-small
                  class="ml-2"
                  color="secondary"
                  outlined
                  >{{ personalCategoryLabel(pi.category) }}</v-chip
                >
                <v-chip
                  v-if="pi.professionalRelevance"
                  x-small
                  class="ml-1"
                  :color="
                    pi.professionalRelevance === 'discuss'
                      ? 'success'
                      : pi.professionalRelevance === 'none'
                      ? 'grey'
                      : 'accent'
                  "
                  outlined
                  >{{ relevanceLabel(pi.professionalRelevance) }}</v-chip
                >
                <v-chip v-if="pi.yearsActive" x-small class="ml-1" outlined
                  >{{ pi.yearsActive }} yr{{
                    pi.yearsActive === 1 ? "" : "s"
                  }}</v-chip
                >
              </div>
              <div>
                <v-btn icon x-small @click="openPersonalDialog(idx)"
                  ><v-icon small>mdi-pencil</v-icon></v-btn
                >
                <v-btn icon x-small @click="removePersonal(idx)"
                  ><v-icon small color="error">mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
            <div v-if="pi.detail" class="text-caption grey--text mt-1">
              {{ pi.detail }}
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- ═════════════════ CAREER INTEREST DIALOG ═════════════════ -->
    <v-dialog v-model="careerDialog" max-width="650" persistent>
      <v-card class="r-card">
        <v-card-title
          >{{ editingCareerIndex !== null ? "Edit" : "Add" }} Career
          Interest</v-card-title
        >
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <!-- PR-INTERESTS-V2: combobox restores the legacy taxonomy
                   autocomplete the old r-multi-select had — users can pick
                   from the seeded list (baseCareerInterests) OR type a
                   free-text entry that isn't in the taxonomy. -->
              <v-combobox
                v-model="careerForm.name"
                :items="careerInterestSuggestions"
                label="Interest Name *"
                dense
                outlined
                hide-no-data
                hide-selected
                clearable
                placeholder="Pick from the list or type your own"
              ></v-combobox>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="careerForm.category"
                :items="careerCategoryOptions"
                item-text="text"
                item-value="value"
                label="Category"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="careerForm.experienceLevel"
                :items="experienceLevelOptions"
                item-text="text"
                item-value="value"
                label="Experience Level"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="careerForm.targetTimeline"
                :items="timelineOptions"
                item-text="text"
                item-value="value"
                label="Target Timeline"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="6"></v-col>
            <v-col cols="12">
              <v-textarea
                v-model="careerForm.whyItMatters"
                label="Why This Matters"
                dense
                outlined
                rows="2"
                placeholder="What's drawing you to this? Helps Sage tailor pathway suggestions."
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-combobox
                v-model="careerForm.associatedSkills"
                label="Associated Skills"
                dense
                outlined
                multiple
                small-chips
                deletable-chips
                placeholder="e.g. SQL, Stakeholder Management"
                hint="Type a skill and press Enter to add it"
              ></v-combobox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="careerDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!careerForm.name"
            @click="saveCareerForm"
            >{{ editingCareerIndex !== null ? "Update" : "Add" }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═════════════════ PERSONAL INTEREST DIALOG ═════════════════ -->
    <v-dialog v-model="personalDialog" max-width="650" persistent>
      <v-card class="r-card">
        <v-card-title
          >{{ editingPersonalIndex !== null ? "Edit" : "Add" }} Personal
          Interest</v-card-title
        >
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="personalForm.name"
                label="Interest Name *"
                dense
                outlined
                placeholder="e.g. Hiking, Pottery, Coaching Youth Basketball"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="personalForm.category"
                :items="personalCategoryOptions"
                item-text="text"
                item-value="value"
                label="Category"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="personalForm.professionalRelevance"
                :items="relevanceOptions"
                item-text="text"
                item-value="value"
                label="Professional Relevance"
                dense
                outlined
                hint="Should this come up in interviews?"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="personalForm.yearsActive"
                label="Years Active"
                dense
                outlined
                type="number"
                min="0"
              ></v-text-field>
            </v-col>
            <v-col cols="6"></v-col>
            <v-col cols="12">
              <v-textarea
                v-model="personalForm.detail"
                label="Detail / Talking Point"
                dense
                outlined
                rows="2"
                placeholder="Anything that turns this from a chip into a conversation."
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="personalDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!personalForm.name"
            @click="savePersonalForm"
            >{{ editingPersonalIndex !== null ? "Update" : "Add" }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </page-main>
</template>

<script>
import * as Display from "../utility/display";
import { mapFields } from "vuex-map-fields";
import { mapActions, mapState } from "vuex";
import Modal from "@/components/Modal.vue";

// PR-INTERESTS-V2: defaults used by both "add new" and the migration helper.
function emptyCareerForm() {
  return {
    id: "",
    name: "",
    category: "",
    targetTimeline: "",
    experienceLevel: "",
    whyItMatters: "",
    associatedSkills: []
  };
}

function emptyPersonalForm() {
  return {
    id: "",
    name: "",
    category: "",
    detail: "",
    professionalRelevance: "",
    yearsActive: null
  };
}

// PR-INTERESTS-V2: vocabularies. Stable identifiers in `value`, user-facing
// strings in `text`. Match the legacy CareerInterest.vue tag values so the
// migration helper can map 1-to-1 without an LLM in the loop.
const CAREER_CATEGORY_OPTIONS = [
  { text: "Target role", value: "target-role" },
  { text: "Industry / sector", value: "industry" },
  { text: "Function / discipline", value: "function" },
  { text: "Skill to grow", value: "skill-to-grow" },
  { text: "Mission / values", value: "mission" }
];

const PERSONAL_CATEGORY_OPTIONS = [
  { text: "Hobby", value: "hobby" },
  { text: "Community / cause", value: "community" },
  { text: "Creative", value: "creative" },
  { text: "Physical / wellness", value: "physical" },
  { text: "Lifelong learning", value: "learning" }
];

const TIMELINE_OPTIONS = [
  { text: "Now", value: "now" },
  { text: "1–2 years", value: "1-2-years" },
  { text: "3–5 years", value: "3-5-years" },
  { text: "Long-term", value: "long-term" }
];

const EXPERIENCE_LEVEL_OPTIONS = [
  { text: "Curious", value: "curious" },
  { text: "Some exposure", value: "some-exposure" },
  { text: "Working in it", value: "working-in-it" },
  { text: "Established", value: "established" }
];

const RELEVANCE_OPTIONS = [
  { text: "Keep private", value: "none" },
  { text: "Builds soft skills", value: "soft-skills" },
  { text: "Adjacent to work", value: "adjacent" },
  { text: "Discuss in interviews", value: "discuss" }
];

export default {
  name: "CareerInterest",
  components: { Modal },
  data: () => ({
    // Dialog state
    careerDialog: false,
    editingCareerIndex: null,
    careerForm: emptyCareerForm(),
    personalDialog: false,
    editingPersonalIndex: null,
    personalForm: emptyPersonalForm(),
    // Migration state
    isMigratingCareer: false,
    isMigratingPersonal: false,
    // Vocabularies (exposed to template)
    careerCategoryOptions: CAREER_CATEGORY_OPTIONS,
    personalCategoryOptions: PERSONAL_CATEGORY_OPTIONS,
    timelineOptions: TIMELINE_OPTIONS,
    experienceLevelOptions: EXPERIENCE_LEVEL_OPTIONS,
    relevanceOptions: RELEVANCE_OPTIONS
  }),
  computed: {
    ...mapFields("careerInterest", [
      "careerInterests",
      "careerInterestsDisplay",
      "baseCareerInterests"
    ]),
    ...mapFields("personalInterest", [
      "personalInterests",
      "personalInterestsDisplay"
    ]),
    ...mapState("careerInterest", ["structuredCareerInterests"]),
    ...mapState("personalInterest", ["structuredPersonalInterests"]),
    legacyCareerCount() {
      return (this.careerInterests || []).filter(
        c => this.itemText(c) && this.itemText(c).trim()
      ).length;
    },
    legacyPersonalCount() {
      return (this.personalInterests || []).filter(
        p => this.itemText(p) && this.itemText(p).trim()
      ).length;
    },
    // PR-INTERESTS-V2: normalize the legacy `baseCareerInterests` taxonomy
    // (the list that used to back the r-multi-select dropdown) into a flat
    // string array v-combobox can consume. Items may arrive as bare strings
    // or as { text, value } objects depending on the seed source — handle
    // both. We also exclude anything already in the structured list so the
    // dropdown doesn't re-suggest things the user already picked.
    careerInterestSuggestions() {
      const taken = new Set(
        (this.structuredCareerInterests || [])
          .map(c => (c && c.name ? c.name.toLowerCase().trim() : ""))
          .filter(s => s)
      );
      return (this.baseCareerInterests || [])
        .map(item => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            return item.text || item.value || item.name || "";
          }
          return "";
        })
        .filter(s => s && !taken.has(s.toLowerCase().trim()));
    },
    tips() {
      return [
        "Keep what you put here appropriate to share in a professional environment.",
        "Be aware of the industry where you're looking for jobs and leave personal items off your resume when appropriate."
      ];
    }
  },
  methods: {
    ...mapActions("careerInterest", [
      "saveCareerInterests",
      "saveStructuredCareerInterests"
    ]),
    ...mapActions("personalInterest", [
      "savePersonalInterests",
      "saveStructuredPersonalInterests"
    ]),

    nav(routerPath) {
      this.$router.push({ path: routerPath });
    },

    // Save called by page-main's footer button. Saves structured AND legacy
    // (so any in-progress legacy edits still go through during migration).
    save() {
      this.findDisplayValues();
      this.saveCareerInterests();
      this.savePersonalInterests();
      this.saveStructuredCareerInterests();
      this.saveStructuredPersonalInterests();
    },

    itemText(item) {
      return item && typeof item === "object" ? item.text : item;
    },

    findDisplayValues() {
      let careerValue = Display.BuildCommaString("", this.careerInterests);
      let personalInterestValue = Display.BuildCommaString(
        "",
        this.personalInterests
      );
      this.careerInterestsDisplay = careerValue;
      this.personalInterestsDisplay = personalInterestValue;
      return [careerValue, personalInterestValue];
    },

    // ───── Label helpers ─────
    careerCategoryLabel(value) {
      const o = CAREER_CATEGORY_OPTIONS.find(x => x.value === value);
      return o ? o.text : value;
    },
    personalCategoryLabel(value) {
      const o = PERSONAL_CATEGORY_OPTIONS.find(x => x.value === value);
      return o ? o.text : value;
    },
    timelineLabel(value) {
      const o = TIMELINE_OPTIONS.find(x => x.value === value);
      return o ? o.text : value;
    },
    experienceLevelLabel(value) {
      const o = EXPERIENCE_LEVEL_OPTIONS.find(x => x.value === value);
      return o ? o.text : value;
    },
    relevanceLabel(value) {
      const o = RELEVANCE_OPTIONS.find(x => x.value === value);
      return o ? o.text : value;
    },

    // ───── Career interest dialog handlers ─────
    openCareerDialog(index) {
      if (index !== undefined && index !== null) {
        this.editingCareerIndex = index;
        this.careerForm = JSON.parse(
          JSON.stringify(this.structuredCareerInterests[index])
        );
        if (!this.careerForm.associatedSkills) {
          this.careerForm.associatedSkills = [];
        }
      } else {
        this.editingCareerIndex = null;
        this.careerForm = emptyCareerForm();
      }
      this.careerDialog = true;
    },
    saveCareerForm() {
      // PR-INTERESTS-V2: v-combobox can return a string (typed input) or
      // the selected object (if items are objects). Our suggestions list
      // is normalized to strings, but defend against both anyway.
      let rawName = this.careerForm.name;
      if (rawName && typeof rawName === "object") {
        rawName = rawName.text || rawName.value || rawName.name || "";
      }
      const name = (rawName || "").toString().trim();
      if (!name) return;
      let updated = [...this.structuredCareerInterests];
      let entry = { ...this.careerForm, name };
      if (!entry.id) entry.id = this._genId("ci");
      if (this.editingCareerIndex !== null) {
        updated.splice(this.editingCareerIndex, 1, entry);
      } else {
        updated.push(entry);
      }
      this.$store.commit(
        "careerInterest/setStructuredCareerInterests",
        updated
      );
      this.saveStructuredCareerInterests(updated);
      this.careerDialog = false;
    },
    removeCareer(index) {
      let updated = [...this.structuredCareerInterests];
      updated.splice(index, 1);
      this.$store.commit(
        "careerInterest/setStructuredCareerInterests",
        updated
      );
      this.saveStructuredCareerInterests(updated);
    },

    // ───── Personal interest dialog handlers ─────
    openPersonalDialog(index) {
      if (index !== undefined && index !== null) {
        this.editingPersonalIndex = index;
        this.personalForm = JSON.parse(
          JSON.stringify(this.structuredPersonalInterests[index])
        );
      } else {
        this.editingPersonalIndex = null;
        this.personalForm = emptyPersonalForm();
      }
      this.personalDialog = true;
    },
    savePersonalForm() {
      if (!this.personalForm.name) return;
      let updated = [...this.structuredPersonalInterests];
      let entry = { ...this.personalForm };
      if (!entry.id) entry.id = this._genId("pi");
      // Years active is a number — coerce blank to null so we don't save 0.
      if (entry.yearsActive === "" || entry.yearsActive === undefined) {
        entry.yearsActive = null;
      }
      if (this.editingPersonalIndex !== null) {
        updated.splice(this.editingPersonalIndex, 1, entry);
      } else {
        updated.push(entry);
      }
      this.$store.commit(
        "personalInterest/setStructuredPersonalInterests",
        updated
      );
      this.saveStructuredPersonalInterests(updated);
      this.personalDialog = false;
    },
    removePersonal(index) {
      let updated = [...this.structuredPersonalInterests];
      updated.splice(index, 1);
      this.$store.commit(
        "personalInterest/setStructuredPersonalInterests",
        updated
      );
      this.saveStructuredPersonalInterests(updated);
    },

    // ───── Migration helpers ─────
    // PR-INTERESTS-V2: walks the legacy StringItem list, copies name +
    // category (when present) into the structured form with defaults for
    // the new fields, saves the structured list, then clears the legacy
    // list so the migration banner doesn't keep firing.
    async migrateLegacyCareer() {
      if (this.legacyCareerCount === 0) return;
      this.isMigratingCareer = true;
      try {
        var existing = new Set(
          (this.structuredCareerInterests || []).map(function(c) {
            return (c.name || "").toLowerCase().trim();
          })
        );
        var added = [];
        for (var i = 0; i < this.careerInterests.length; i++) {
          var item = this.careerInterests[i];
          var name = (this.itemText(item) || "").trim();
          if (!name || existing.has(name.toLowerCase())) continue;
          existing.add(name.toLowerCase());
          added.push({
            id: this._genId("ci"),
            name: name,
            category: (item && item.category) || "",
            targetTimeline: "",
            experienceLevel: "",
            whyItMatters: "",
            associatedSkills: []
          });
        }
        var merged = (this.structuredCareerInterests || []).concat(added);
        this.$store.commit(
          "careerInterest/setStructuredCareerInterests",
          merged
        );
        await this.saveStructuredCareerInterests(merged);
        // Clear the legacy list so the banner stops showing. saveCareer-
        // Interests will write the cleared list back through the legacy
        // endpoint so the next load doesn't re-hydrate it.
        this.careerInterests = [];
        this.careerInterestsDisplay = "";
        await this.saveCareerInterests();
      } finally {
        this.isMigratingCareer = false;
      }
    },

    async migrateLegacyPersonal() {
      if (this.legacyPersonalCount === 0) return;
      this.isMigratingPersonal = true;
      try {
        var existing = new Set(
          (this.structuredPersonalInterests || []).map(function(p) {
            return (p.name || "").toLowerCase().trim();
          })
        );
        var added = [];
        for (var i = 0; i < this.personalInterests.length; i++) {
          var item = this.personalInterests[i];
          var name = (this.itemText(item) || "").trim();
          if (!name || existing.has(name.toLowerCase())) continue;
          existing.add(name.toLowerCase());
          added.push({
            id: this._genId("pi"),
            name: name,
            category: (item && item.category) || "",
            detail: "",
            professionalRelevance: "",
            yearsActive: null
          });
        }
        var merged = (this.structuredPersonalInterests || []).concat(added);
        this.$store.commit(
          "personalInterest/setStructuredPersonalInterests",
          merged
        );
        await this.saveStructuredPersonalInterests(merged);
        this.personalInterests = [];
        this.personalInterestsDisplay = "";
        await this.savePersonalInterests();
      } finally {
        this.isMigratingPersonal = false;
      }
    },

    // Stable-enough ID generator — same shape used elsewhere in the app
    // for new keys; not security-sensitive.
    _genId(prefix) {
      return (
        prefix +
        "-" +
        Date.now().toString(36) +
        "-" +
        Math.random()
          .toString(36)
          .slice(2, 8)
      );
    }
  }
};
</script>

<style scoped>
.empty-state {
  text-align: center;
  padding: 24px 16px;
  border: 1px dashed rgba(160, 160, 160, 0.5);
  border-radius: 8px;
  margin-bottom: 12px;
}
.empty-state-icon {
  font-size: 36px;
  margin-bottom: 6px;
  opacity: 0.6;
}
.empty-state-title {
  font-weight: 600;
  margin-bottom: 4px;
}
.empty-state-subtitle {
  font-size: 13px;
  opacity: 0.75;
}
</style>
